/**
 * Mobile M0 — register service worker + optional web-push subscription.
 */

function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (err) {
    console.warn("[pwa] service worker registration failed", err);
    return null;
  }
}

type WebPushRegistration = {
  endpoint: string;
  expirationTime?: number | null;
  keys: { p256dh: string; auth: string };
};

type PushClient = {
  notifications: {
    getVapidPublicKey: { query: () => Promise<{ publicKey: string; configured: boolean }> };
    registerWebPush: {
      mutate: (input: { subscription: WebPushRegistration }) => Promise<{ id: number }>;
    };
  };
};

function toRegistration(json: PushSubscriptionJSON): WebPushRegistration | null {
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
}

/** Subscribe to web push and persist the subscription via tRPC. */
export async function enableWebPush(
  client: PushClient,
): Promise<"subscribed" | "denied" | "unsupported" | "unconfigured"> {
  if (!("Notification" in window) || !("PushManager" in window) || !("serviceWorker" in navigator)) {
    return "unsupported";
  }
  const { publicKey, configured } = await client.notifications.getVapidPublicKey.query();
  if (!configured || !publicKey) return "unconfigured";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const registration = await registerServiceWorker();
  if (!registration) return "unsupported";
  // ready never rejects when registration is absent or activation fails.
  // Bound the wait so Settings cannot remain stuck on "enabling".
  let timer: ReturnType<typeof setTimeout> | undefined;
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 10000); }),
  ]).finally(() => clearTimeout(timer));
  if (!reg) return "unsupported";

  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const payload = toRegistration(subscription.toJSON());
  if (!payload) return "unsupported";

  await client.notifications.registerWebPush.mutate({ subscription: payload });
  return "subscribed";
}
